import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  LogOut,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  Store,
  ShoppingCart,
  WalletCards,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/boost/Logo";
import {
  createStore,
  deleteStore,
  getMentor,
  getStores,
  isMentorLoggedIn,
  logoutMentor,
  updateStore,
} from "@/services/businessService";

const emptyForm = {
  store_name: "",
  revenue: 0,
  orders: 0,
  products_sold: 0,
  refunds: 0,
  discounts: 0,
  product_cost: 0,
  meta_ads: 0,
  google_ads: 0,
  shipping: 0,
  payment_fees: 0,
  other_costs: 0,
  period_start: "",
  period_end: "",
};

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-US");

const fields = [
  ["store_name", "Store Name", "text"],
  ["revenue", "Revenue", "number"],
  ["orders", "Orders", "number"],
  ["products_sold", "Products Sold", "number"],
  ["refunds", "Refunds", "number"],
  ["discounts", "Discounts", "number"],
  ["product_cost", "Product Cost / COGS", "number"],
  ["meta_ads", "Meta Ads", "number"],
  ["google_ads", "Google Ads", "number"],
  ["shipping", "Shipping", "number"],
  ["payment_fees", "Payment Fees", "number"],
  ["other_costs", "Other Costs", "number"],
  ["period_start", "Period Start", "date"],
  ["period_end", "Period End", "date"],
];

export default function MentorDashboard() {
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mentor, setMentor] = useState(null);

  const load = async () => {
    setError("");

    try {
      const [me, data] = await Promise.all([
        getMentor(),
        getStores(),
      ]);

      setMentor(me);
      setStores(data);
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        logoutMentor();
        navigate("/mentor/login", { replace: true });
        return;
      }

      setError(err.message || "Unable to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isMentorLoggedIn()) {
      navigate("/mentor/login", { replace: true });
      return;
    }

    load();
  }, []);

  const filtered = useMemo(
    () =>
      stores.filter((s) =>
        String(s.store_name || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [stores, search]
  );

  const totals = useMemo(() => {
    const revenue = stores.reduce(
      (a, s) => a + Number(s.revenue || 0),
      0
    );

    const orders = stores.reduce(
      (a, s) => a + Number(s.orders || 0),
      0
    );

    const products = stores.reduce(
      (a, s) => a + Number(s.products_sold || 0),
      0
    );

    const profit = stores.reduce(
      (a, s) => a + Number(s.estimated_profit || 0),
      0
    );

    return {
      revenue,
      orders,
      products,
      profit,
      margin: revenue ? (profit / revenue) * 100 : 0,
      aov: orders ? revenue / orders : 0,
    };
  }, [stores]);

  const setField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]:
        key === "store_name" || key.startsWith("period_")
          ? value
          : Number(value),
    }));
  };

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError("");

    try {
      const payload = {
        ...form,
        store_name: String(form.store_name || "").trim(),
      };

      if (!payload.store_name) {
        setError("Store name is required.");
        setSaving(false);
        return;
      }

      if (editingId) {
        await updateStore(editingId, payload);
      } else {
        await createStore(payload);
      }

      reset();
      await load();
    } catch (err) {
      setError(err.message || "Unable to save store.");
    } finally {
      setSaving(false);
    }
  };

  const edit = (store) => {
    setEditingId(store.id);

    const next = { ...emptyForm };

    Object.keys(next).forEach((key) => {
      next[key] = store[key] ?? next[key];
    });

    setForm(next);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const remove = async (store) => {
    if (
      !window.confirm(
        `Delete ${store.store_name}? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteStore(store.id);
      await load();
    } catch (err) {
      setError(err.message || "Unable to delete store.");
    }
  };

  const cards = [
    ["Total Revenue", money(totals.revenue), WalletCards],
    ["Estimated Profit", money(totals.profit), TrendingUp],
    ["Total Orders", number(totals.orders), ShoppingCart],
    ["Stores", number(stores.length), Store],
    ["Products Sold", number(totals.products), ShoppingCart],
    [
      "Profit Margin",
      `${totals.margin.toFixed(1)}%`,
      TrendingUp,
    ],
    [
      "Average Order Value",
      money(totals.aov),
      WalletCards,
    ],
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="container-x flex items-center justify-between gap-4 py-3">
          <Link to="/">
            <Logo />
          </Link>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-500">
              {mentor?.email}
            </span>

            <button
              onClick={() => {
                logoutMentor();
                navigate("/mentor/login", {
                  replace: true,
                });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-8 md:py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="chip">Private Mentor Area</div>

            <h1 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-tight">
              BoostF Business Hub
            </h1>

            <p className="mt-2 text-slate-600">
              Manage store performance and the aggregate numbers shown publicly.
            </p>
          </div>

          <Link
            to="/results"
            className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Public Results
          </Link>
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map(([label, value, Icon]) => (
            <div
              key={label}
              className="rounded-3xl bg-white border border-slate-100 p-5 shadow-premium"
            >
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center">
                <Icon className="w-5 h-5" />
              </div>

              <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-slate-500">
                {label}
              </div>

              <div className="mt-1 font-display text-2xl sm:text-3xl font-black">
                {value}
              </div>
            </div>
          ))}
        </section>

        <section className="mt-8 rounded-[32px] bg-white border border-slate-100 shadow-premium p-6 md:p-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="chip">
                {editingId ? "Edit Store" : "Add Store"}
              </div>

              <h2 className="mt-2 font-display text-2xl font-black">
                {editingId
                  ? "Update business data"
                  : "Add a business store"}
              </h2>
            </div>

            {editingId && (
              <button
                onClick={reset}
                className="text-sm font-semibold text-slate-500 hover:text-slate-900"
              >
                Cancel edit
              </button>
            )}
          </div>

          <form
            onSubmit={submit}
            className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {fields.map(([key, label, type]) => (
              <label key={key} className="block">
                <span className="text-xs font-semibold text-slate-600">
                  {label}
                </span>

                <input
                  name={key}
                  required={key === "store_name"}
                  type={type}
                  min={
                    type === "number"
                      ? "0"
                      : undefined
                  }
                  step={
                    type === "number"
                      ? "0.01"
                      : undefined
                  }
                  value={form[key]}
                  onChange={(e) =>
                    setField(key, e.target.value)
                  }
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
                />
              </label>
            ))}

            <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary disabled:opacity-60"
              >
                <Plus className="w-4 h-4" />

                {saving
                  ? "Saving…"
                  : editingId
                  ? "Save Changes"
                  : "Add Store"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-full border border-slate-200 px-6 py-3 font-semibold hover:bg-slate-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="mt-8 rounded-[32px] bg-white border border-slate-100 shadow-premium overflow-hidden">
          <div className="p-6 md:p-8 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="chip">Store Performance</div>

              <h2 className="mt-2 font-display text-2xl font-black">
                Business stores
              </h2>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

              <input
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                placeholder="Search store…"
                className="rounded-full border border-slate-200 pl-9 pr-4 py-2.5 outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500">
              Loading stores…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              {stores.length
                ? "No matching stores."
                : "No stores added yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-4">
                      Store
                    </th>

                    <th className="text-right px-5 py-4">
                      Revenue
                    </th>

                    <th className="text-right px-5 py-4">
                      Orders
                    </th>

                    <th className="text-right px-5 py-4">
                      Refunds
                    </th>

                    <th className="text-right px-5 py-4">
                      Ad Spend
                    </th>

                    <th className="text-right px-5 py-4">
                      Product Cost
                    </th>

                    <th className="text-right px-5 py-4">
                      Profit
                    </th>

                    <th className="text-right px-5 py-4">
                      Margin
                    </th>

                    <th className="text-right px-5 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      className="hover:bg-slate-50/70"
                    >
                      <td className="px-5 py-4 font-semibold">
                        {s.store_name}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {money(s.revenue)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {number(s.orders)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {money(s.refunds)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {money(
                          Number(s.meta_ads || 0) +
                            Number(s.google_ads || 0)
                        )}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {money(s.product_cost)}
                      </td>

                      <td className="px-5 py-4 text-right font-bold text-emerald-700">
                        {money(s.estimated_profit)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        {Number(
                          s.profit_margin || 0
                        ).toFixed(1)}
                        %
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => edit(s)}
                            className="w-9 h-9 rounded-full border border-slate-200 grid place-items-center hover:bg-slate-50"
                            aria-label={`Edit ${s.store_name}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => remove(s)}
                            className="w-9 h-9 rounded-full border border-red-100 text-red-600 grid place-items-center hover:bg-red-50"
                            aria-label={`Delete ${s.store_name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}